import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from '@/features/notifications/hooks/useNotifications';
import { GUIDE_ROUTES } from '@/features/guide/constants/routes';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UserRole } from '@/types';

function formatRelative(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function NotificationBell() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { data: notificationsData, isLoading } = useNotifications({ page: 1, limit: 20 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  if (!isAuthenticated) return null;

  const notifications = notificationsData?.data ?? [];

  const handleNotificationClick = async (id: string, bookingId?: string, link?: string) => {
    await markRead.mutateAsync(id);
    setOpen(false);
    // Server-provided in-app paths (admin alerts) win over the role defaults.
    if (link?.startsWith('/')) {
      navigate(link);
      return;
    }
    if (bookingId) {
      if (user?.role === UserRole.SUPER_ADMIN) {
        navigate(ADMIN_ROUTES.booking(bookingId));
      } else if (user?.role === UserRole.GUIDE) {
        navigate(GUIDE_ROUTES.BOOKINGS);
      } else {
        navigate('/my-bookings');
      }
    }
  };

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        aria-label="notifications"
        sx={{ color: 'inherit' }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
            },
          }}
        >
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 380 },
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              sx={{ color: 'primary.main' }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {notifications.map((n) => {
              const bookingId = (n.data?.bookingId ?? n.data?.guideRequestId) as string | undefined;
              const link = typeof n.data?.link === 'string' ? n.data.link : undefined;
              return (
                <ListItem key={n._id} disablePadding divider>
                  <ListItemButton
                    onClick={() => handleNotificationClick(n._id, bookingId, link)}
                    sx={{
                      py: 1.5,
                      '&:hover': { bgcolor: 'primary.50' },
                    }}
                  >
                    <ListItemText
                      primary={n.title}
                      secondary={
                        <>
                          {n.message}
                          <Typography component="span" variant="caption" display="block" color="text.disabled">
                            {formatRelative(n.createdAt)}
                          </Typography>
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Drawer>
    </>
  );
}
